import React, { useRef, useEffect, useState } from 'react';
import paper from 'paper';
import ProgressBar from '../ProgressBar';

const Drift = ({
  particleCount = 100,
  particleColor = 'white',
  particleRadius = 2,
  gravity = 0.06,
  damping = 0.97,
  wanderStrength = 0.1,
  maxSpeed = 4,
  fillColor = 'white',
  barColor = 'rgba(255,255,255,0.55)',
  documentBackgroundColor = '#050510',
  texts = [
    { text: 'thoughts before language', count: 300, fontSize: 36 },
    { text: 'they move without direction', count: 700, fontSize: 36 },
    { text: 'you are gravity', count: 1100, fontSize: 54 },
    { text: 'still', count: 1500, fontSize: 120 },
  ],
}) => {
  const canvasRef = useRef(null);
  // ref for fast in-closure tracking; state for React re-renders
  const barRef = useRef({ index: 0, counter: 0 });
  const [barProgress, setBarProgress] = useState({ index: 0, counter: 0 });

  useEffect(() => {
    paper.setup(canvasRef.current);

    const w = paper.view.size.width;
    const h = paper.view.size.height;

    const mouse = { x: w / 2, y: h / 2 };

    // particles
    const particles = [];
    for (let i = 0; i < particleCount; i++) {
      const pos = new paper.Point(Math.random() * w, Math.random() * h);
      const r = particleRadius * (0.5 + Math.random() * 1.5);
      const shape = new paper.Path.Circle(pos, r);
      shape.fillColor = particleColor;
      shape.opacity = 0.25 + Math.random() * 0.75;
      particles.push({
        shape,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
      });
    }

    const displayText = new paper.PointText({
      point: [w / 2, h / 2],
      content: texts[0].text,
      justification: 'center',
      fontSize: texts[0].fontSize,
    });
    displayText.fillColor = fillColor;

    const onMouseMove = (event) => {
      mouse.x = event.point.x;
      mouse.y = event.point.y;
      document.body.style.backgroundColor = documentBackgroundColor;
      displayText.point = event.point;

      // progress tracking
      const bar = barRef.current;
      if (bar.index < texts.length - 1) bar.counter += 1;
      if (bar.counter > texts[bar.index].count) {
        bar.index += 1;
        displayText.content = texts[bar.index].text;
        displayText.fontSize = texts[bar.index].fontSize;
      }
      setBarProgress({ index: bar.index, counter: bar.counter });
    };

    const onFrame = () => {
      particles.forEach((p) => {
        const px = p.shape.position.x;
        const py = p.shape.position.y;

        const dx = mouse.x - px;
        const dy = mouse.y - py;
        const dist = Math.sqrt(dx * dx + dy * dy) + 1;

        p.vx += (dx / dist) * gravity;
        p.vy += (dy / dist) * gravity;

        p.vx += (Math.random() - 0.5) * wanderStrength;
        p.vy += (Math.random() - 0.5) * wanderStrength;

        p.vx *= damping;
        p.vy *= damping;

        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > maxSpeed) {
          p.vx = (p.vx / speed) * maxSpeed;
          p.vy = (p.vy / speed) * maxSpeed;
        }

        let nx = px + p.vx;
        let ny = py + p.vy;
        if (nx < 0) nx = w;
        if (nx > w) nx = 0;
        if (ny < 0) ny = h;
        if (ny > h) ny = 0;

        p.shape.position = new paper.Point(nx, ny);
      });
    };

    paper.view.on('mousemove', onMouseMove);
    paper.view.on('frame', onFrame);

    return () => {
      paper.view.off('mousemove', onMouseMove);
      paper.view.off('frame', onFrame);
      paper.project.clear();
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', cursor: 'none' }} />
      <ProgressBar
        texts={texts}
        index={barProgress.index}
        counter={barProgress.counter}
        color={barColor}
      />
    </div>
  );
};

export default Drift;
